import React, { useState, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface TutorialStep {
  title: string;
  description: string;
  image?: string;
}

interface TutorialOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  steps?: TutorialStep[];
  onComplete?: () => void;
}

export default function TutorialOverlay({ 
  isOpen, 
  onClose, 
  steps = [],
  onComplete
}: TutorialOverlayProps) {
  const [currentStep, setCurrentStep] = useState(0);

  // Memoize steps to prevent unnecessary re-renders
  const validSteps = useMemo(() => {
    return Array.isArray(steps) ? steps : [];
  }, [steps]);

  const nextStep = () => {
    if (currentStep < validSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
      if (onComplete) onComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Safely get the current step, defaulting to an empty object if no steps
  const currentTutorialStep = validSteps[currentStep] || {
    title: 'Tutorial',
    description: 'No tutorial steps available.'
  };

  // If no steps, don't render the full overlay
  if (validSteps.length === 0) {
    return null;
  }

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      {currentTutorialStep.title}
                    </Dialog.Title>
                    <div className="mt-4">
                      {currentTutorialStep.image && (
                        <img 
                          src={currentTutorialStep.image} 
                          alt={currentTutorialStep.title} 
                          className="mb-4 rounded-lg w-full"
                        />
                      )}
                      <p className="text-sm text-gray-500">
                        {currentTutorialStep.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    onClick={nextStep}
                  >
                    {currentStep < validSteps.length - 1 ? 'Next' : 'Finish'}
                  </button>
                  {currentStep > 0 && (
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={prevStep}
                    >
                      Previous
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}